// IndexedDB utility for storing query history and results
const DB_NAME = 'AIAnalyticsQueries';
const DB_VERSION = 1;
const STORE_NAME = 'queryHistory';

class QueryStorageDB {
  constructor() {
    this.db = null;
    this.initPromise = this.init();
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          
          // Create indexes
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('prompt', 'prompt', { unique: false });
        }
      };
    });
  }

  async ensureReady() {
    if (!this.db) {
      await this.initPromise;
    }
  }

  async saveQuery(queryData) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const queryRecord = {
        ...queryData,
        timestamp: Date.now()
      };
      
      const request = store.add(queryRecord);
      
      request.onsuccess = () => {
        resolve({ ...queryRecord, id: request.result });
      };
      
      request.onerror = () => {
        console.error('Failed to save query:', request.error);
        reject(request.error);
      };
    });
  }

  async getQueryHistory(limit = 10) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');
      
      // Get queries in descending order (newest first)
      const request = index.openCursor(null, 'prev');
      const results = [];
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        
        if (cursor && results.length < limit) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      
      request.onerror = () => {
        console.error('Failed to get query history:', request.error);
        reject(request.error);
      };
    });
  }

  async getQueryById(id) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = () => {
        console.error('Failed to get query by ID:', request.error);
        reject(request.error);
      };
    });
  }

  async deleteQuery(id) {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = () => {
        console.error('Failed to delete query:', request.error);
        reject(request.error);
      };
    });
  }

  async clearAllQueries() {
    await this.ensureReady();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = () => {
        console.error('Failed to clear all queries:', request.error);
        reject(request.error);
      };
    });
  }

  async updateQuery(id, updates) {
    await this.ensureReady();
    
    return new Promise(async (resolve, reject) => {
      try {
        const existing = await this.getQueryById(id);
        if (!existing) {
          reject(new Error('Query not found'));
          return;
        }

        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const updatedQuery = { ...existing, ...updates };
        
        const request = store.put(updatedQuery);
        
        request.onsuccess = () => {
          resolve(updatedQuery);
        };
        
        request.onerror = () => {
          console.error('Failed to update query:', request.error);
          reject(request.error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }
}

// Create and export a singleton instance
const queryStorageDB = new QueryStorageDB();
export default queryStorageDB; 